'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { PageHeader } from '@/components/ui/page-header';
import { TableCard } from '@/components/ui/table-card';
import { useTablePage } from '@/hooks/use-table-page';
import { useExternalRequestsList } from '@/lib/api/external-requests';
import {
  externalRequestsFilterMapping,
  externalRequestsSortMapping,
  externalRequestsQueryParamsMapping,
} from './utils/table-mappings';

import { ExternalRequestsTable } from './components/ExternalRequestsTable';
import { EventSimulator } from './components/EventSimulator';
import type { ExternalRequestLog } from './components/external-requests-table-columns';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(ExternalRequestsPage, PERMISSIONS.STORES_VIEW);

function ExternalRequestsPage() {
  const { t } = useTranslation('externalRequests');
  const [activeTab, setActiveTab] = React.useState('logs');
  const [autoReload, setAutoReload] = React.useState(false);

  // Use generic table page hook - sort by most recent first
  const { searchQuery, setSearchQuery, tableState, setTableState, queryInput } = useTablePage({
    initialSorting: [{ id: 'createdAt', desc: true }],
    filterMapping: externalRequestsFilterMapping,
    sortMapping: externalRequestsSortMapping,
    queryParamsMapping: externalRequestsQueryParamsMapping,
  });

  // Use tRPC hook to fetch external requests with auto-reload
  const {
    data: logsResponse,
    isLoading: loading,
    isFetching: fetching,
    error,
    refetch,
  } = useExternalRequestsList(queryInput, autoReload);

  // Auto-reload effect
  React.useEffect(() => {
    if (!autoReload) return;

    const interval = setInterval(() => {
      refetch();
    }, 5000); // Refresh every 5 seconds

    return () => clearInterval(interval);
  }, [autoReload, refetch]);

  const logs = (logsResponse?.logs || []) as ExternalRequestLog[];
  const total = logsResponse?.total || 0;
  const pageCount = logsResponse?.pageCount || 0;
  const stats = logsResponse?.stats;

  // Extract options from response
  const options = React.useMemo(() => {
    const backendOptions = (logsResponse as any)?.options;
    if (!backendOptions) {
      return {
        service: [],
        eventType: [],
        requestType: [],
        status: [],
        storeId: [], // { id, name }[]
      };
    }
    return {
      service: backendOptions.service || [],
      eventType: backendOptions.eventType || [],
      requestType: backendOptions.requestType || [],
      status: backendOptions.status || [],
      storeId: backendOptions.storeId || [], // { id, name }[]
    };
  }, [logsResponse]);

  const handleEventSent = () => {
    // Refetch logs after event is sent
    setTimeout(() => {
      refetch();
    }, 2000); // Wait 2 seconds for event to be processed
  };

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      {/* Page header */}
      <PageHeader title={t('title')} description={t('description')} />

      {/* Stats cards - Commented out for now, can be enabled later with ResourceStatsCardGrid */}
      {/* {stats && (
        <ResourceStatsCardGrid
          cards={[
            {
              title: 'Total Requests',
              value: stats.totalLogs.toLocaleString(),
              description: 'All time',
              icon: Activity,
            },
            {
              title: 'Successful',
              value: stats.successCount.toLocaleString(),
              description: `${stats.totalLogs > 0 ? Math.round((stats.successCount / stats.totalLogs) * 100) : 0}%`,
              icon: CheckCircle,
            },
            {
              title: 'Failed',
              value: stats.failedCount.toLocaleString(),
              description: `${stats.totalLogs > 0 ? Math.round((stats.failedCount / stats.totalLogs) * 100) : 0}%`,
              icon: XCircle,
            },
            {
              title: 'Services',
              value: Object.keys(stats.serviceBreakdown).length.toString(),
              description: Object.keys(stats.serviceBreakdown).slice(0, 2).join(', '),
              icon: Zap,
            },
          ]}
          loading={loading}
        />
      )} */}

      {/* Tabs for Logs and Simulator */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="logs">{t('tabs.logs')}</TabsTrigger>
          <TabsTrigger value="simulator">{t('tabs.simulator')}</TabsTrigger>
        </TabsList>

        <TabsContent value="logs" className="space-y-4">
          <TableCard>
            <ExternalRequestsTable
              logs={logs}
              total={total}
              pageCount={pageCount}
              options={options}
              isLoading={loading}
              isFetching={fetching}
              autoReload={autoReload}
              onAutoReloadChange={setAutoReload}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onTableStateChange={setTableState}
              onRefetch={refetch}
            />

            {/* Error message */}
            {error && (
              <div className="mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
                {error instanceof Error ? error.message : 'Failed to load external requests'}
              </div>
            )}
          </TableCard>
        </TabsContent>

        <TabsContent value="simulator">
          <EventSimulator onEventSent={handleEventSent} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

